import React, { useState, ReactNode } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface PasswordProtectionProps {
  password: string;
  children: ReactNode;
}

const PasswordProtection: React.FC<PasswordProtectionProps> = ({ password, children }) => {
  const [inputPassword, setInputPassword] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (inputPassword === password) {
      setAuthorized(true);
      setError(false);
      
      // Store auth status in session storage to persist during the session
      sessionStorage.setItem('passwordAuth', 'true');
    } else {
      setError(true);
    }
  };

  // Check if already authorized from session storage
  React.useEffect(() => {
    const isAuthorized = sessionStorage.getItem('passwordAuth') === 'true';
    if (isAuthorized) {
      setAuthorized(true);
    }
  }, []);

  if (authorized) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-indigo-50">
      <header className="bg-indigo-950 text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-2xl font-serif font-medium">Protected Content</h1>
        </div>
      </header>
      
      <main className="flex-grow flex items-center justify-center py-12">
        <div className="bg-white p-8 rounded-sm shadow-lg border border-indigo-100 max-w-md w-full">
          <h2 className="text-xl font-serif text-center mb-6">Password Required</h2>
          
          <p className="text-gray-600 mb-6 text-center text-sm">
            This content is protected. Please enter the password to continue.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="password"
                value={inputPassword}
                onChange={(e) => setInputPassword(e.target.value)}
                placeholder="Enter password"
                className={`w-full ${error ? 'border-red-500' : ''}`}
                autoFocus
              />
              {error && (
                <p className="text-red-500 text-sm mt-1">Incorrect password. Please try again.</p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-indigo-800 hover:bg-indigo-700 text-white"
            >
              Submit
            </Button>
          </form>
        </div>
      </main>
      
      <footer className="bg-indigo-950 text-white py-4">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-indigo-200">
            © {new Date().getFullYear()} Next Generation Medicine. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PasswordProtection;